import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { api, fmtNum, fmtDate, openPdf } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { useAuth } from "@finance/context/AuthContext";
import { exportToCsv } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@finance/components/ui/tabs";
import CoaManager from "@finance/components/CoaManager";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { toast } from "sonner";
import { DownloadSimple, Plus, Trash } from "@phosphor-icons/react";
import { LockedPeriodBanner } from "@finance/components/LockedPeriodBanner";
import { QuickAddButton } from "@finance/components/QuickAdd";
import { SearchableSelect } from "@finance/components/SearchableSelect";
import { BulkUploadButton } from "@finance/components/BulkUpload";

const today = () => new Date().toISOString().slice(0, 10);
const emptyLine = () => ({ account_code: "", debit: "", credit: "", currency: "TZS", fx_rate: "" });

export default function GeneralLedger() {
  const [tb, setTb] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [bs, setBs] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [account, setAccount] = useState("");
  const [ledger, setLedger] = useState(null);
  const [tab, setTab] = useState("tb");
  const [journals, setJournals] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const { user } = useAuth();
  const isExec = !!user?.is_executive;
  const isOwner = !!user?.is_owner;
  const [jOpen, setJOpen] = useState(false);
  const [jForm, setJForm] = useState({ date: today(), memo: "", cost_centre_id: "none", lines: [emptyLine(), emptyLine()] });
  const [ccFilter, setCcFilter] = useState("all");
  const [entries, setEntries] = useState([]);
  const [revFor, setRevFor] = useState(null);
  const [revForm, setRevForm] = useState({ date: today(), reason: "" });
  const [pendingRevs, setPendingRevs] = useState([]);
  const [revStatus, setRevStatus] = useState("");
  const [revBy, setRevBy] = useState("");
  const [rejFor, setRejFor] = useState(null);
  const [rejNote, setRejNote] = useState("");

  const refresh = () => {
    const params = ccFilter !== "all" ? { cost_centre: ccFilter } : {};
    api.get("/gl/trial-balance", { params }).then((r) => setTb(r.data));
    api.get("/gl/pnl", { params }).then((r) => setPnl(r.data));
    api.get("/gl/balance-sheet", { params }).then((r) => setBs(r.data));
    api.get("/gl/journals").then((r) => setJournals(r.data));
    api.get("/gl/journal-entries").then((r) => setEntries(r.data.entries)).catch(() => {});
    api.get("/gl/reversal-requests").then((r) => setPendingRevs(r.data)).catch(() => {});
    api.get("/currencies").then((r) => setCurrencies(r.data)).catch(() => {});
  };

  const editJournal = (j) => {
    setJForm({ id: j.source_id, date: (j.date || "").slice(0, 10), memo: j.memo || "",
      cost_centre_id: j.cost_centre_id || "none",
      lines: (j.lines || []).map((l) => ({ account_code: l.account_code, debit: (l.orig_debit ?? l.debit) || "", credit: (l.orig_credit ?? l.credit) || "", currency: l.currency || "TZS", fx_rate: l.fx_rate || "" })) });
    setJOpen(true);
  };
  const delJournal = async (j) => {
    if (!window.confirm(`Delete this manual journal (${j.memo || (j.source_id || "").slice(0, 8)})? This reverses its GL postings.`)) return;
    try { await api.delete(`/gl/journal/${j.source_id}`); toast.success("Journal deleted"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete journal"); }
  };
  const doReverse = async () => {
    if (!revForm.reason.trim()) { toast.error("Enter a reason for the reversal"); return; }
    try {
      const { data } = await api.post(`/gl/entries/${revFor.id}/reverse`, { date: revForm.date, reason: revForm.reason.trim() });
      toast.success(data.posted ? `Reversed — posted mirror entry #${data.reversal_ref}` : "Reversal request submitted — awaiting Ronick's approval");
      setRevFor(null); setRevForm({ date: today(), reason: "" }); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reverse entry"); }
  };
  const approveRev = async (r) => {
    try { const { data } = await api.post(`/gl/reversal-requests/${r.id}/approve`); toast.success(`Approved — posted mirror entry #${data.reversal_ref}`); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to approve"); }
  };
  const rejectRev = async () => {
    try { await api.post(`/gl/reversal-requests/${rejFor.id}/reject`, { note: rejNote.trim() }); toast.success("Reversal request rejected — nothing posted"); setRejFor(null); setRejNote(""); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to reject"); }
  };
  const pendingRevCount = pendingRevs.filter((r) => r.status === "pending").length;
  const [logStatus, setLogStatus] = useState("all");
  const [logSearch, setLogSearch] = useState("");
  const logRows = pendingRevs.filter((r) => {
    if (logStatus !== "all" && r.status !== logStatus) return false;
    if (logSearch) { const q = logSearch.toLowerCase(); return [r.requested_by_name, r.reason, r.entry_memo].some((x) => (x || "").toLowerCase().includes(q)); }
    return true;
  });
  const logExportRows = logRows.map((r) => ({
    requested: fmtDate(r.requested_at),
    entry: `#${r.entry_ref} ${r.entry_memo || r.entry_source_type || ""}`.trim(),
    amount: r.entry_amount || 0,
    reason: r.reason || "",
    raised_by: `${r.requested_by_name || ""}${r.direct ? " (direct)" : ""}`,
    status: r.status,
    decision_by: r.status === "approved" ? (r.approved_by_name || "") : r.status === "rejected" ? (r.rejected_by_name || "") : "",
    decision_date: r.status === "approved" ? fmtDate(r.approved_at) : r.status === "rejected" ? fmtDate(r.rejected_at) : "",
    reversal_ref: r.reversal_ref || "",
    note: r.reject_note || "",
  }));
  const logCols = [
    { key: "requested", label: "Requested" }, { key: "entry", label: "Entry" },
    { key: "amount", label: "Amount", format: (v) => fmtNum(v) }, { key: "reason", label: "Reason" },
    { key: "raised_by", label: "Raised By" }, { key: "status", label: "Status" },
    { key: "decision_by", label: "Decision By" }, { key: "decision_date", label: "Decision Date" },
    { key: "reversal_ref", label: "Reversal Ref" }, { key: "note", label: "Note" },
  ];
  const printReversalLog = () => {
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const body = logRows.map((r) => `<tr><td>${esc(fmtDate(r.requested_at))}</td><td>#${esc(r.entry_ref)} ${esc(r.entry_memo || r.entry_source_type)}</td><td style="text-align:right">${esc(fmtNum(r.entry_amount || 0))}</td><td>${esc(r.reason)}</td><td>${esc(r.requested_by_name)}${r.direct ? " (direct)" : ""}</td><td>${r.status === "approved" ? `Approved by ${esc(r.approved_by_name)} · ${esc(fmtDate(r.approved_at))} · #${esc(r.reversal_ref)}` : r.status === "rejected" ? `Rejected by ${esc(r.rejected_by_name)} · ${esc(fmtDate(r.rejected_at))}${r.reject_note ? " — " + esc(r.reject_note) : ""}` : "Pending"}</td></tr>`).join("");
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to export the PDF"); return; }
    w.document.write(`<html><head><title>Reversal Log</title><style>body{font-family:Arial,sans-serif;padding:24px;color:#1a1a1a}h1{color:#1B4383;font-size:18px;margin:0 0 4px}.meta{font-size:12px;color:#666;margin-bottom:12px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #e5e7eb;padding:6px 8px;text-align:left;vertical-align:top}th{background:#f5f5f5}</style></head><body><h1>Somcore Finance — Reversal Log</h1><div class="meta">Generated ${new Date().toLocaleString()} · ${logRows.length} record(s)${logStatus !== "all" ? ` · status: ${logStatus}` : ""}</div><table><thead><tr><th>Requested</th><th>Entry</th><th>Amount (TSh)</th><th>Reason</th><th>Raised by</th><th>Decision</th></tr></thead><tbody>${body}</tbody></table></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 350);
  };

  const exportExcel = () => {
    import("xlsx").then((XLSX) => {
      const wb = XLSX.utils.book_new();
      if (pnl) {
        const rows = [["PROFIT & LOSS"]];
        [["Revenue", pnl.revenue_groups], ["Cost of Sales", pnl.cogs_groups]].forEach(([t, gs]) => {
          rows.push([t]); gs.forEach((g) => { rows.push(["  " + g.name, g.total]); g.accounts.forEach((a) => rows.push(["    " + a.name, a.amount])); });
        });
        rows.push(["Gross Profit", pnl.gross_profit]); rows.push(["Operating Expenses"]);
        pnl.opex_groups.forEach((g) => { rows.push(["  " + g.name, g.total]); g.accounts.forEach((a) => rows.push(["    " + a.name, a.amount])); });
        rows.push(["Net Profit", pnl.net_profit]);
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "P&L");
      }
      if (bs) {
        const rows = [["BALANCE SHEET"], ["Assets"], ...bs.assets.map((a) => ["  " + a.name, a.amount]), ["Total Assets", bs.total_assets],
          ["Liabilities"], ...bs.liabilities.map((a) => ["  " + a.name, a.amount]), ["Equity"], ...bs.equity.map((a) => ["  " + a.name, a.amount])];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Balance Sheet");
      }
      if (tb) {
        const rows = [["Code", "Account", "Debit", "Credit"], ...tb.rows.map((r) => [r.code, r.name, r.debit_balance, r.credit_balance]), ["", "Total", tb.total_debit, tb.total_credit]];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Trial Balance");
      }
      XLSX.writeFile(wb, "financial-statements.xlsx");
    });
  };
  const lineRate = (l) => ((l.currency || "TZS") === "TZS" ? 1 : (Number(l.fx_rate) || 0));
  const jTotals = jForm.lines.reduce((a, l) => ({ d: a.d + (Number(l.debit) || 0) * lineRate(l), c: a.c + (Number(l.credit) || 0) * lineRate(l) }), { d: 0, c: 0 });
  const setLine = (i, k, v) => { const ls = [...jForm.lines]; ls[i] = { ...ls[i], [k]: v }; setJForm({ ...jForm, lines: ls }); };
  const setLineCurrency = (i, v) => {
    const ls = [...jForm.lines];
    let fx = ls[i].fx_rate;
    if (v !== "TZS") { const c = currencies.find((x) => x.code === v); if (c?.latest_rate) fx = Math.round(Number(c.latest_rate) * 10000) / 10000; }
    else fx = "";
    ls[i] = { ...ls[i], currency: v, fx_rate: fx };
    setJForm({ ...jForm, lines: ls });
  };
  const postJournal = async () => {
    if (!jForm.memo) { toast.error("Enter a memo/description"); return; }
    if (jForm.lines.some((l) => (l.currency && l.currency !== "TZS") && (Number(l.debit) || Number(l.credit)) && !(Number(l.fx_rate) > 0))) { toast.error("Enter an FX rate for every foreign-currency line"); return; }
    if (Math.abs(jTotals.d - jTotals.c) > 0.01 || jTotals.d === 0) { toast.error("Debits must equal credits (in TZS) and be non-zero"); return; }
    try {
      const payload = { date: jForm.date, memo: jForm.memo, cost_centre_id: jForm.cost_centre_id !== "none" ? jForm.cost_centre_id : null, lines: jForm.lines.filter((l) => l.account_code && (Number(l.debit) || Number(l.credit))).map((l) => ({ account_code: l.account_code, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0, currency: l.currency || "TZS", fx_rate: (l.currency && l.currency !== "TZS") ? (Number(l.fx_rate) || null) : null })) };
      const res = jForm.id ? await api.put(`/gl/journal/${jForm.id}`, payload) : await api.post("/gl/journal", payload);
      if (res.data?.pending_cash_request) { toast.info(res.data.message); setJOpen(false); setJForm({ date: today(), memo: "", cost_centre_id: "none", lines: [emptyLine(), emptyLine()] }); return; }
      toast.success(jForm.id ? "Journal updated" : "Journal posted"); setJOpen(false);
      setJForm({ date: today(), memo: "", cost_centre_id: "none", lines: [emptyLine(), emptyLine()] }); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post journal"); }
  };

  const drill = (code) => { setAccount(code); setTab("ledger"); };

  const loadReports = useCallback(() => {
    const params = ccFilter !== "all" ? { cost_centre: ccFilter } : {};
    api.get("/gl/trial-balance", { params }).then((r) => setTb(r.data));
    api.get("/gl/pnl", { params }).then((r) => setPnl(r.data));
    api.get("/gl/balance-sheet", { params }).then((r) => setBs(r.data));
  }, [ccFilter]);

  useEffect(() => { loadReports(); }, [loadReports]);

  useEffect(() => {
    api.get("/gl/accounts").then((r) => setAccounts(r.data));
    api.get("/gl/cost-centres").then((r) => setCostCentres(r.data));
    api.get("/currencies").then((r) => setCurrencies(r.data)).catch(() => {});
    api.get("/gl/journals").then((r) => setJournals(r.data)).catch(() => {});
    api.get("/gl/journal-entries").then((r) => setEntries(r.data.entries)).catch(() => {});
    api.get("/gl/reversal-requests").then((r) => setPendingRevs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (account) api.get("/gl/entries", { params: { account } }).then((r) => setLedger(r.data));
  }, [account]);

  return (
    <Layout>
      <PageHeader title="General Ledger" subtitle="Double-entry postings · trial balance · P&L · balance sheet"
        actions={<div className="flex items-center gap-2">
          <Select value={ccFilter} onValueChange={setCcFilter}>
            <SelectTrigger className="w-48" data-testid="cc-filter"><SelectValue placeholder="All cost centres" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All cost centres</SelectItem>{costCentres.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" data-testid="export-financials-pdf" onClick={() => openPdf(`pdf/financials${ccFilter !== "all" ? `?cost_centre=${ccFilter}` : ""}`)}><DownloadSimple size={16} className="mr-1" />PDF</Button>
          <Button variant="outline" data-testid="export-financials-xlsx" onClick={exportExcel}><DownloadSimple size={16} className="mr-1" />Excel</Button>
          <BulkUploadButton entity="gl_accounts" label="Bulk Accounts" onDone={() => { api.get("/gl/accounts").then((r) => setAccounts(r.data)); loadReports(); }} />
          <BulkUploadButton entity="gl_journals" label="Bulk Journals" onDone={loadReports} />
          <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="new-journal-btn" onClick={() => setJOpen(true)}><Plus size={16} className="mr-1" />New Journal Entry</Button>
        </div>}
      />
      <div className="p-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="gl-tabs">
            <TabsTrigger value="tb" data-testid="tab-tb">Trial Balance</TabsTrigger>
            <TabsTrigger value="pnl" data-testid="tab-pnl">Profit &amp; Loss</TabsTrigger>
            <TabsTrigger value="bs" data-testid="tab-bs">Balance Sheet</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger Detail</TabsTrigger>
            <TabsTrigger value="journals" data-testid="tab-journals">Manual Journals</TabsTrigger>
            <TabsTrigger value="entries" data-testid="tab-entries">Journal Entries</TabsTrigger>
            <TabsTrigger value="reversals" data-testid="tab-reversals">Pending Reversals{pendingRevCount > 0 ? <span className="ml-1.5 rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white" data-testid="pending-rev-badge">{pendingRevCount}</span> : null}</TabsTrigger>
            <TabsTrigger value="revlog" data-testid="tab-revlog">Reversal Log</TabsTrigger>
            <TabsTrigger value="coa" data-testid="tab-coa">Chart of Accounts</TabsTrigger>
          </TabsList>

          <TabsContent value="tb" className="mt-6">
            {tb && (
              <>
                <div className="mb-3 flex justify-end">
                  <Button variant="outline" data-testid="export-tb" onClick={() => exportToCsv("trial-balance", tb.rows, [
                    { key: "code", label: "Code" }, { key: "name", label: "Account" }, { key: "type", label: "Type" },
                    { key: "debit_balance", label: "Debit", format: fmtNum }, { key: "credit_balance", label: "Credit", format: fmtNum },
                  ])}><DownloadSimple size={16} className="mr-1" />Export</Button>
                </div>
                <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                      <th className="px-4 py-2 text-left">Code</th><th className="px-4 py-2 text-left">Account</th>
                      <th className="px-4 py-2 text-left">Type</th><th className="px-4 py-2 text-right">Debit</th>
                      <th className="px-4 py-2 text-right">Credit</th>
                    </tr></thead>
                    <tbody>
                      {tb.rows.filter((r) => r.debit_balance || r.credit_balance).map((r) => (
                        <tr key={r.code} data-testid={`tb-row-${r.code}`} onClick={() => drill(r.code)} className="cursor-pointer border-b border-gray-100 hover:bg-orange-50">
                          <td className="px-4 py-2 font-mono-num">{r.code}</td><td className="px-4 py-2 underline decoration-dotted">{r.name}</td>
                          <td className="px-4 py-2 text-gray-500">{r.type}</td>
                          <td className="px-4 py-2 text-right lx-num">{r.debit_balance ? fmtNum(r.debit_balance, "TZS") : "—"}</td>
                          <td className="px-4 py-2 text-right lx-num">{r.credit_balance ? fmtNum(r.credit_balance, "TZS") : "—"}</td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-gray-300 font-bold">
                        <td className="px-4 py-2" colSpan={3}>TOTAL</td>
                        <td className="px-4 py-2 text-right lx-num">{fmtNum(tb.total_debit, "TZS")}</td>
                        <td className="px-4 py-2 text-right lx-num">{fmtNum(tb.total_credit, "TZS")}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="pnl" className="mt-6">
            {pnl && (
              <div className="max-w-2xl rounded-sm border border-gray-200 bg-white p-6" data-testid="pnl-report">
                <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">Gross Profit by Brand</div>
                {(pnl.brand_blocks || []).map((b) => (
                  <div key={b.brand} className="mb-3" data-testid={`brand-block-${b.brand.replace(/\s+/g, "-")}`}>
                    <div className="flex justify-between text-sm"><span className="font-medium">{b.brand} — Sales</span><span className="lx-num">{fmtNum(b.sales, "TZS")}</span></div>
                    <div className="flex justify-between text-sm text-gray-500"><span>Less: Cost of Sales</span><span className="lx-num">({fmtNum(b.cogs, "TZS")})</span></div>
                    <div className="flex justify-between border-t border-gray-100 pt-0.5 text-sm font-semibold"><span>{b.brand} Gross Profit</span><span className="lx-num">{fmtNum(b.gross_profit, "TZS")}</span></div>
                  </div>
                ))}
                <div className="mt-4 border-t-2 border-gray-200 pt-2">
                  <Subtotal label="Consolidated Revenue" value={pnl.consolidated_revenue} />
                  <Subtotal label="Consolidated Cost of Sales" value={pnl.consolidated_cogs} />
                  <Subtotal label="Consolidated Gross Profit" value={pnl.consolidated_gross_profit} testid="gross-profit" bold />
                </div>
                <GroupSection title="Operating Expenses" groups={pnl.opex_groups} total={pnl.total_opex} onPick={drill} />
                <Subtotal label="Net Profit" value={pnl.net_profit} bold testid="net-profit" />
              </div>
            )}
          </TabsContent>

          <TabsContent value="bs" className="mt-6">
            {bs && (
              <div className="max-w-2xl rounded-sm border border-gray-200 bg-white p-6">
                <Section title="Assets" rows={bs.assets} total={bs.total_assets} onPick={drill} />
                <Section title="Liabilities" rows={bs.liabilities} total={bs.total_liabilities} onPick={drill} />
                <Section title="Equity" rows={bs.equity} total={bs.total_equity} onPick={drill} />
                <div className="mt-4 flex items-center justify-between rounded-sm bg-gray-50 px-3 py-2 text-sm" data-testid="bs-inventory-recon">
                  <span className="text-gray-600">Inventory reconciliation — GL 1200 vs perpetual stock value</span>
                  <span className="lx-num">{fmtNum(bs.inventory_gl_balance, "TZS")} vs {fmtNum(bs.inventory_stock_value, "TZS")}
                    {bs.inventory_reconciled
                      ? <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">✓ Reconciled</span>
                      : <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">Δ {fmtNum(bs.inventory_variance, "TZS")}</span>}
                  </span>
                </div>
                <div className="mt-2 text-xs text-gray-500">{bs.balanced ? "✓ Balanced (Assets = Liabilities + Equity)" : "⚠ Not balanced — check postings"}</div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="ledger" className="mt-6">
            <div className="mb-4 w-72">
              <SearchableSelect testid="ledger-account" value={account} onValueChange={setAccount}
                placeholder="Select account"
                options={accounts.map((a) => ({ value: a.code, label: `${a.code} · ${a.name}` }))} />
            </div>
            {ledger && (
              <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-left">Supplier</th>
                    <th className="px-4 py-2 text-left">Memo</th>
                    <th className="px-4 py-2 text-left">Source</th><th className="px-4 py-2 text-right">Debit</th>
                    <th className="px-4 py-2 text-right">Credit</th>
                  </tr></thead>
                  <tbody>
                    {ledger.entries.length === 0 && <TableEmpty colSpan={6} title="No entries" message="Ledger entries for this account will appear here." />}
                    {ledger.entries.map((e, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="px-4 py-2 lx-num">{fmtDate(e.date)}</td>
                        <td className="px-4 py-2 text-gray-700" data-testid={`ledger-supplier-${i}`}>{e.supplier || "—"}</td>
                        <td className="px-4 py-2">{e.memo}</td>
                        <td className="px-4 py-2 text-gray-500">{e.source_type}</td>
                        <td className="px-4 py-2 text-right lx-num">{e.debit ? fmtNum(e.debit, "TZS") : "—"}</td>
                        <td className="px-4 py-2 text-right lx-num">{e.credit ? fmtNum(e.credit, "TZS") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="journals" className="mt-6">
            <div className="overflow-hidden rounded-sm border border-gray-200 bg-white" data-testid="manual-journals-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Memo</th>
                  <th className="px-4 py-2 text-left">Accounts</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {journals.length === 0 && <TableEmpty colSpan={5} title="No manual journals yet" message={`Use "New Journal Entry" to post one.`} />}
                  {journals.map((j) => {
                    const amt = (j.lines || []).reduce((s, l) => s + (Number(l.debit) || 0), 0);
                    return (
                      <tr key={j.source_id} data-testid={`manual-journal-${j.source_id}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2 lx-num whitespace-nowrap">{fmtDate(j.date)}</td>
                        <td className="px-4 py-2">{j.memo || "—"}</td>
                        <td className="px-4 py-2 text-xs text-gray-500">{(j.lines || []).map((l) => l.account_code).join(", ")}</td>
                        <td className="px-4 py-2 text-right lx-num">{fmtNum(amt, "TZS")}</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <button className="text-xs text-gray-500 hover:text-[#1B4383]" data-testid={`edit-journal-${j.source_id}`} onClick={() => editJournal(j)}>Edit</button>
                          {!j.reversed_by && !j.reverses && !j.reversal_pending && <button className="ml-3 text-xs text-amber-600 hover:underline" data-testid={`reverse-journal-${j.source_id}`} onClick={() => { setRevFor({ id: j.id, memo: j.memo, source_type: "manual", amount: (j.lines || []).reduce((s, l) => s + (Number(l.debit) || 0), 0) }); setRevForm({ date: today(), reason: "" }); }}>{isOwner ? "Reverse" : "Request Reverse"}</button>}
                          {j.reversal_pending && <span className="ml-3 text-xs text-amber-600" data-testid={`journal-rev-pending-${j.source_id}`}>Reversal pending</span>}
                          {j.reversed_by && <span className="ml-3 text-xs text-red-500" data-testid={`journal-reversed-${j.source_id}`}>Reversed #{j.reversed_by_ref}</span>}
                          {isExec && <button className="ml-3 text-xs text-gray-400 hover:text-red-500" data-testid={`delete-journal-${j.source_id}`} onClick={() => delJournal(j)}>Delete</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-gray-500">Only manually-posted journals appear here. System journals (from POs, invoices, payments, expenses, payroll) are amended by editing their source document. Edits reverse &amp; repost; deletes are restricted to executives.</p>
          </TabsContent>

          <TabsContent value="entries" className="mt-6">
            <div className="overflow-hidden rounded-sm border border-gray-200 bg-white" data-testid="journal-entries-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Memo</th>
                  <th className="px-4 py-2 text-left">Source</th>
                  <th className="px-4 py-2 text-left">Accounts</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {entries.length === 0 && <TableEmpty colSpan={7} title="No journal entries" message="Posted entries across all modules appear here." />}
                  {entries.map((e) => (
                    <tr key={e.id} data-testid={`journal-entry-${e.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2 lx-num whitespace-nowrap">{fmtDate(e.date)}</td>
                      <td className="px-4 py-2 max-w-[280px] truncate" title={e.memo}>{e.memo || "—"}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{e.source_type}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{(e.accounts || []).join(", ")}</td>
                      <td className="px-4 py-2 text-right lx-num">{fmtNum(e.amount, "TZS")}</td>
                      <td className="px-4 py-2 text-xs">
                        {e.reversed_by ? <span className="rounded-sm bg-red-50 px-1.5 py-0.5 text-red-600" data-testid={`entry-status-${e.id}`}>Reversed by #{e.reversed_by_ref}</span>
                          : e.reversal_pending ? <span className="rounded-sm bg-amber-50 px-1.5 py-0.5 text-amber-700" data-testid={`entry-status-${e.id}`}>Reversal pending</span>
                          : e.is_reversal ? <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-gray-600" data-testid={`entry-status-${e.id}`}>Reverses #{e.reverses_ref}</span>
                          : <span className="rounded-sm bg-emerald-50 px-1.5 py-0.5 text-emerald-700" data-testid={`entry-status-${e.id}`}>Posted</span>}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {e.reversible
                          ? <button className="text-xs font-medium text-amber-600 hover:underline" data-testid={`reverse-entry-${e.id}`} onClick={() => { setRevFor(e); setRevForm({ date: today(), reason: "" }); }}>{isOwner ? "Reverse" : "Request Reverse"}</button>
                          : <span className="text-xs text-gray-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-gray-500">One-click reversal posts a mirror journal (Dr/Cr flipped) dated as of the reversal date, netting the entry to zero. The original is preserved for audit and both entries cross-link. Inventory / cost postings are excluded — correct the source document instead.</p>
          </TabsContent>

          <TabsContent value="reversals" className="mt-6">
            <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="reversal-audit-bar">
              <span className="lx-label text-gray-500">Reversal Audit</span>
              <select className="h-8 rounded-sm border border-gray-300 px-2 text-sm" data-testid="reversal-audit-status" value={revStatus} onChange={(e) => setRevStatus(e.target.value)}>
                <option value="">All statuses</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
              </select>
              <input className="h-8 rounded-sm border border-gray-300 px-2 text-sm" placeholder="Requested by" data-testid="reversal-audit-by" value={revBy} onChange={(e) => setRevBy(e.target.value)} />
              <Button size="sm" variant="outline" data-testid="reversal-audit-csv" onClick={async () => {
                const r = await api.get("/gl/reversal-audit", { params: { status: revStatus || undefined, requested_by: revBy || undefined, fmt: "csv" }, responseType: "blob" });
                const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "reversal-audit.csv"; a.click();
              }}>Export CSV</Button>
              <Button size="sm" variant="outline" data-testid="reversal-audit-pdf" onClick={() => window.print()}>Print / PDF</Button>
            </div>
            <div className="overflow-hidden rounded-sm border border-gray-200 bg-white" data-testid="reversal-requests-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                  <th className="px-4 py-2 text-left">Requested</th>
                  <th className="px-4 py-2 text-left">Entry</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-left">Reason</th>
                  <th className="px-4 py-2 text-left">By</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {pendingRevs.length === 0 && <TableEmpty colSpan={7} title="No reversal requests" message="Reversal requests raised by the finance team appear here for approval." />}
                  {pendingRevs.filter((r) => (!revStatus || r.status === revStatus) && (!revBy || (r.requested_by_name || "").toLowerCase().includes(revBy.toLowerCase()))).map((r) => (
                    <tr key={r.id} data-testid={`reversal-request-${r.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2 lx-num whitespace-nowrap">{fmtDate(r.requested_at)}</td>
                      <td className="px-4 py-2"><span className="font-mono-num text-xs text-gray-500">#{r.entry_ref}</span> <span className="max-w-[220px] truncate align-middle">{r.entry_memo || r.entry_source_type}</span></td>
                      <td className="px-4 py-2 text-right lx-num">{fmtNum(r.entry_amount, "TZS")}</td>
                      <td className="px-4 py-2 max-w-[220px] truncate" title={r.reason}>{r.reason}</td>
                      <td className="px-4 py-2 text-gray-600">{r.requested_by_name}</td>
                      <td className="px-4 py-2 text-xs">
                        {r.status === "pending" ? <span className="rounded-sm bg-amber-50 px-1.5 py-0.5 text-amber-700">Pending</span>
                          : r.status === "approved" ? <span className="rounded-sm bg-emerald-50 px-1.5 py-0.5 text-emerald-700">Approved #{r.reversal_ref}</span>
                          : <span className="rounded-sm bg-red-50 px-1.5 py-0.5 text-red-600" title={r.reject_note || ""}>Rejected</span>}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {r.status === "pending" && isOwner ? (
                          <>
                            <button className="text-xs font-medium text-emerald-600 hover:underline" data-testid={`approve-reversal-${r.id}`} onClick={() => approveRev(r)}>Approve</button>
                            <button className="ml-3 text-xs text-red-500 hover:underline" data-testid={`reject-reversal-${r.id}`} onClick={() => { setRejFor(r); setRejNote(""); }}>Reject</button>
                          </>
                        ) : r.status === "pending" ? <span className="text-xs text-gray-400">Awaiting authoriser</span> : <span className="text-xs text-gray-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-gray-500">Finance staff (Finance, Finance, Finance) raise reversal requests; only Ronick can approve them. On approval the mirror journal posts and the original is marked reversed. Rejecting leaves the original untouched.</p>
          </TabsContent>

          <TabsContent value="revlog" className="mt-6">
            <div className="mb-3 flex items-center gap-2">
              <Select value={logStatus} onValueChange={setLogStatus}>
                <SelectTrigger className="w-40" data-testid="revlog-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent>
              </Select>
              <Input className="w-72" placeholder="Search memo, reason or person" data-testid="revlog-search" value={logSearch} onChange={(e) => setLogSearch(e.target.value)} />
              <div className="ml-auto flex items-center gap-2">
                <Button variant="outline" size="sm" data-testid="revlog-export-csv" disabled={!logRows.length} onClick={() => exportToCsv("reversal-log", logExportRows, logCols)}><DownloadSimple size={15} className="mr-1" />Excel/CSV</Button>
                <Button variant="outline" size="sm" data-testid="revlog-export-pdf" disabled={!logRows.length} onClick={printReversalLog}><DownloadSimple size={15} className="mr-1" />PDF</Button>
              </div>
            </div>
            <div className="overflow-hidden rounded-sm border border-gray-200 bg-white" data-testid="revlog-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                  <th className="px-4 py-2 text-left">Requested</th>
                  <th className="px-4 py-2 text-left">Entry</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-left">Reason</th>
                  <th className="px-4 py-2 text-left">Raised by</th>
                  <th className="px-4 py-2 text-left">Decision</th>
                </tr></thead>
                <tbody>
                  {logRows.length === 0 && <TableEmpty colSpan={6} title="No reversals" message="Reversal requests and posted reversals appear here." />}
                  {logRows.map((r) => (
                    <tr key={r.id} data-testid={`revlog-row-${r.id}`} className="border-b border-gray-100">
                      <td className="px-4 py-2 lx-num whitespace-nowrap">{fmtDate(r.requested_at)}</td>
                      <td className="px-4 py-2"><span className="font-mono-num text-xs text-gray-500">#{r.entry_ref}</span> {r.entry_memo || r.entry_source_type}</td>
                      <td className="px-4 py-2 text-right lx-num">{fmtNum(r.entry_amount, "TZS")}</td>
                      <td className="px-4 py-2 max-w-[220px] truncate" title={r.reason}>{r.reason}</td>
                      <td className="px-4 py-2 text-gray-600">{r.requested_by_name}{r.direct ? " (direct)" : ""}</td>
                      <td className="px-4 py-2 text-xs">
                        {r.status === "pending" ? <span className="text-amber-600">Pending Ronick</span>
                          : r.status === "approved" ? <span className="text-emerald-600">Approved by {r.approved_by_name} · {fmtDate(r.approved_at)} · #{r.reversal_ref}</span>
                          : <span className="text-red-500">Rejected by {r.rejected_by_name} · {fmtDate(r.rejected_at)}{r.reject_note ? ` — ${r.reject_note}` : ""}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-gray-500">Full audit trail of every reversal — requests raised by staff and direct reversals by the owner, with who approved/rejected, when and why.</p>
          </TabsContent>

          <TabsContent value="coa" className="mt-6">
            <CoaManager />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={jOpen} onOpenChange={setJOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="journal-dialog">
          <DialogHeader><DialogTitle className="font-display">{jForm.id ? "Edit Journal Entry" : "New Journal Entry"}</DialogTitle></DialogHeader>
          <LockedPeriodBanner date={jForm.date} className="mb-2" />
          <div className="grid grid-cols-4 gap-4">
            <div><Label className="lx-label">Date</Label><DateField className="mt-1" data-testid="journal-date" value={jForm.date} onChange={(e) => setJForm({ ...jForm, date: e.target.value })} /></div>
            <div className="col-span-2"><Label className="lx-label">Memo / Description</Label><Input className="mt-1" data-testid="journal-memo" value={jForm.memo} onChange={(e) => setJForm({ ...jForm, memo: e.target.value })} /></div>
            <div><Label className="lx-label">Cost Centre</Label>
              <Select value={jForm.cost_centre_id} onValueChange={(v) => setJForm({ ...jForm, cost_centre_id: v })}>
                <SelectTrigger className="mt-1" data-testid="journal-cost-centre"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{costCentres.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-2 space-y-2">
            {jForm.lines.map((l, i) => {
              const ccy = l.currency || "TZS";
              const r = ccy === "TZS" ? 1 : (Number(l.fx_rate) || 0);
              const tzs = ((Number(l.debit) || 0) || (Number(l.credit) || 0)) * r;
              return (
              <div key={i} className="rounded-md border border-gray-100 p-2 dark:border-[#243049]" data-testid={`journal-line-${i}`}>
                <div className="grid grid-cols-12 items-center gap-2">
                  <div className="col-span-4">
                    <div className="flex items-center gap-1">
                      <SearchableSelect className="h-9 flex-1 text-xs" testid={`journal-account-${i}`} value={l.account_code || ""} placeholder="Account"
                        onValueChange={(v) => setLine(i, "account_code", v)}
                        options={accounts.map((a) => ({ value: a.code, label: `${a.code} · ${a.name}` }))} />
                      <QuickAddButton type="account" onCreated={(a) => { api.get("/gl/accounts").then((r) => setAccounts(r.data)); setLine(i, "account_code", a.code); }} />
                    </div>
                  </div>
                  <Select value={ccy} onValueChange={(v) => setLineCurrency(i, v)}>
                    <SelectTrigger className="col-span-2 h-9 text-xs" data-testid={`journal-currency-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["TZS", ...currencies.map((c) => c.code).filter((c) => c && c !== "TZS")].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input className="col-span-2" type="number" placeholder="Debit" data-testid={`journal-debit-${i}`} value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} />
                  <Input className="col-span-2" type="number" placeholder="Credit" data-testid={`journal-credit-${i}`} value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} />
                  <button className="col-span-2 flex items-center justify-center text-gray-400 hover:text-red-500" data-testid={`journal-remove-${i}`} onClick={() => setJForm({ ...jForm, lines: jForm.lines.filter((_, j) => j !== i) })}><Trash size={16} /></button>
                </div>
                {ccy !== "TZS" && (
                  <div className="mt-1.5 flex items-center gap-2 pl-1 text-[11px] text-gray-500">
                    <span className="lx-label">Rate {ccy}→TZS</span>
                    <Input className="h-7 w-28" type="number" data-testid={`journal-rate-${i}`} value={l.fx_rate || ""} onChange={(e) => setLine(i, "fx_rate", e.target.value)} />
                    <span className="lx-num text-gray-400">≈ TSh {fmtNum(tzs)}</span>
                  </div>
                )}
              </div>
              );
            })}
            <Button variant="outline" size="sm" data-testid="journal-add-line" onClick={() => setJForm({ ...jForm, lines: [...jForm.lines, emptyLine()] })}><Plus size={14} className="mr-1" />Add Line</Button>
          </div>
          <div className="mt-2 flex justify-end gap-6 text-sm">
            <div><span className="lx-label">Debits</span> <span className="lx-num ml-2">{fmtNum(jTotals.d, "TZS")}</span></div>
            <div><span className="lx-label">Credits</span> <span className="lx-num ml-2">{fmtNum(jTotals.c, "TZS")}</span></div>
            <div className={Math.abs(jTotals.d - jTotals.c) < 0.01 ? "text-emerald-600" : "text-red-500"}>{Math.abs(jTotals.d - jTotals.c) < 0.01 ? "Balanced" : "Out of balance"}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="post-journal" onClick={postJournal}>{jForm.id ? "Update Journal" : "Post Journal"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!revFor} onOpenChange={(v) => { if (!v) { setRevFor(null); setRevForm({ date: today(), reason: "" }); } }}>
        <DialogContent className="max-w-md" data-testid="reverse-dialog">
          <DialogHeader><DialogTitle className="font-display">{isOwner ? "Reverse Entry" : "Request Reversal"}</DialogTitle></DialogHeader>
          {revFor && (
            <div className="space-y-3">
              <div className="rounded-sm bg-gray-50 p-3 text-sm">
                <div className="font-medium text-gray-800">{revFor.memo || "GL entry"}</div>
                <div className="text-xs text-gray-500">{revFor.source_type ? `${revFor.source_type} · ` : ""}TSh {fmtNum(revFor.amount || 0)}</div>
              </div>
              <div className="rounded-sm bg-amber-50 px-3 py-2 text-xs text-amber-700">{isOwner ? "A mirror journal (debits/credits flipped) will be posted, netting this entry to zero. The original stays untouched for audit; both entries cross-link." : "This creates a pending request — nothing posts until Ronick approves it. The original entry is not changed in the meantime."}</div>
              <LockedPeriodBanner date={revForm.date} className="mb-1" />
              <div><Label className="lx-label">Reversal date</Label><DateField className="mt-1" data-testid="reverse-date" value={revForm.date} onChange={(e) => setRevForm({ ...revForm, date: e.target.value })} /></div>
              <div><Label className="lx-label">Reason <span className="text-red-500">*</span></Label><Input className="mt-1" data-testid="reverse-reason" value={revForm.reason} onChange={(e) => setRevForm({ ...revForm, reason: e.target.value })} placeholder="Why is this being reversed?" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevFor(null)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="confirm-reverse" onClick={doReverse} disabled={!revForm.reason.trim()}>{isOwner ? "Post Reversal" : "Submit Request"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejFor} onOpenChange={(v) => { if (!v) { setRejFor(null); setRejNote(""); } }}>
        <DialogContent className="max-w-md" data-testid="reject-reversal-dialog">
          <DialogHeader><DialogTitle className="font-display">Reject Reversal Request</DialogTitle></DialogHeader>
          {rejFor && (
            <div className="space-y-3">
              <div className="rounded-sm bg-gray-50 p-3 text-sm">
                <div className="font-medium text-gray-800">{rejFor.entry_memo || rejFor.entry_source_type}</div>
                <div className="text-xs text-gray-500">Requested by {rejFor.requested_by_name} · Reason: {rejFor.reason}</div>
              </div>
              <div><Label className="lx-label">Note (optional)</Label><Input className="mt-1" data-testid="reject-reversal-note" value={rejNote} onChange={(e) => setRejNote(e.target.value)} placeholder="Why is this being rejected?" /></div>
              <div className="text-xs text-gray-500">Nothing will post — the original entry stays exactly as is.</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejFor(null); setRejNote(""); }}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="confirm-reject-reversal" onClick={rejectRev}>Reject Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function GroupSection({ title, groups, total, onPick }) {
  return (
    <div className="mb-4">
      <div className="lx-label mb-1 font-semibold text-gray-700">{title}</div>
      {(groups || []).length === 0 && <div className="py-1 text-sm text-gray-400">—</div>}
      {(groups || []).map((g) => (
        <div key={g.name} className="mb-2">
          <div className="flex justify-between py-0.5 text-sm font-medium text-gray-800">
            <span>{g.name}</span><span className="lx-num">{fmtNum(g.total, "TZS")}</span>
          </div>
          {g.accounts.map((r) => (
            <div key={r.code} onClick={() => r.code && onPick?.(r.code)} className="flex cursor-pointer justify-between py-0.5 pl-4 text-sm text-gray-500 hover:text-[#1B4383]">
              <span>{r.name}</span><span className="lx-num">{fmtNum(r.amount, "TZS")}</span>
            </div>
          ))}
        </div>
      ))}
      <div className="mt-1 flex justify-between border-t border-gray-200 py-1 text-sm font-semibold">
        <span>Total {title}</span><span className="lx-num">{fmtNum(total, "TZS")}</span>
      </div>
    </div>
  );
}

function Subtotal({ label, value, bold, testid }) {
  return (
    <div data-testid={testid} className={`mt-3 flex justify-between border-t-2 border-gray-300 pt-3 ${bold ? "font-display text-lg font-bold" : "font-semibold"}`}>
      <span>{label}</span>
      <span className={`lx-num ${value >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtNum(value, "TZS")}</span>
    </div>
  );
}


function Section({ title, rows, total, onPick }) {
  return (
    <div className="mb-4">
      <div className="lx-label mb-1">{title}</div>
      {rows.map((r) => (
        <div key={r.code} onClick={() => r.code && r.code !== "NI" && onPick?.(r.code)} className={`flex justify-between py-1 text-sm ${r.code && r.code !== "NI" ? "cursor-pointer hover:text-[#1B4383]" : ""}`}>
          <span className="text-gray-700">{r.name}</span>
          <span className="lx-num">{fmtNum(r.amount, "TZS")}</span>
        </div>
      ))}
      <div className="mt-1 flex justify-between border-t border-gray-200 py-1 text-sm font-medium">
        <span>Total {title}</span><span className="lx-num">{fmtNum(total, "TZS")}</span>
      </div>
    </div>
  );
}
